import { db } from '../../db/indexeddb'
import {
  getCloudHabitLogs,
  getCloudHabitLogIds,
  getCloudHabitLogsByIds,
  getDeletedCloudHabitLogs,
  createCloudHabitLog,
  updateCloudHabitLog,
  deleteCloudHabitLog,
} from '../../supabase/queries'
import { CloudHabitLogSchema, CloudHabitLogPayloadSchema } from '../sync-schemas'
import { resolveCloudHabitId, resolveLocalHabitId } from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { HabitLog } from '../../db/models'

/* ── Habit log ─────────────────────────────────────────── */

// Depends on its parent habit's cloud id. Defers push until the habit is synced.

export const habitLogPullDescriptor: PullDescriptor<any> = {
  entityName: 'habitLog',
  settingsKey: 'lastHabitLogSyncTime',

  fetchRemote: (userId, since) => getCloudHabitLogs(userId, since),
  fetchRemoteIds: (userId) => getCloudHabitLogIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudHabitLogsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudHabitLogs(userId, since),

  safeParse: (raw) => CloudHabitLogSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.habitLogs,
  getAllLocal: () => db.habitLogs.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.habitLogs.filter(l => !l.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => {
    const localHabitId = await resolveLocalHabitId(remote.habit_id, remote.habit_local_id)
    if (!localHabitId) return null // parent habit not synced locally yet — retry next cycle
    // Guard against a duplicate (habitId, date) row created independently on this device.
    const existing = await db.habitLogs.where('[habitId+date]').equals([localHabitId, remote.date]).first()
    if (existing) return null
    return {
      id: crypto.randomUUID(),
      habitId: localHabitId,
      date: remote.date,
      completed: remote.completed,
      userId: remote.user_id,
      cloudId: remote.id,
      deletedAt: null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote) => ({
    completed: remote.completed,
    updatedAt: new Date(remote.updated_at),
    ...(remote.deleted_at ? { deletedAt: new Date(remote.deleted_at) } : { deletedAt: null }),
  }),
}

export const habitLogPushDescriptor: PushDescriptor<HabitLog> = {
  entityName: 'CloudHabitLog',
  getLocal: async (id) => (await db.habitLogs.get(id)) ?? null,
  getLocalCloudId: (l) => l.cloudId,
  getLocalId: (l) => l.id,
  table: db.habitLogs,

  canCreate: async (log) => {
    const cloudHabitId = await resolveCloudHabitId(log.habitId)
    return cloudHabitId ? { defer: false } : { defer: true }
  },

  canUpdate: async (log) => {
    const cloudHabitId = await resolveCloudHabitId(log.habitId)
    return cloudHabitId ? { defer: false } : { defer: true }
  },

  buildCreatePayload: async (log) => {
    const cloudHabitId = await resolveCloudHabitId(log.habitId)
    return CloudHabitLogPayloadSchema.parse({
      habit_id: cloudHabitId,
      habit_local_id: log.habitId,
      date: log.date,
      completed: log.completed,
      local_id: log.id,
    })
  },

  buildUpdatePayload: async (log) => {
    const cloudHabitId = await resolveCloudHabitId(log.habitId)
    const payload: Record<string, any> = CloudHabitLogPayloadSchema.parse({
      habit_id: cloudHabitId,
      habit_local_id: log.habitId,
      date: log.date,
      completed: log.completed,
      local_id: log.id,
    })
    if (!log.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudHabitLog(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudHabitLog(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudHabitLog(cloudId, userId),
}
